import { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { Download, Loader2, Plus, RotateCcw, Save, Send, Trash2, X } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { api, errMsg } from "./api";

const PAD = 0.9;
const MIN = 0.1;
const SNAP = 0.06;
const path = (g, H) => [g.outer, ...g.holes].map((r) => "M" + r.map(([x, y]) => `${x},${H - y}`).join("L") + "Z").join(" ");
const uniform = (L, s) => { const n = Math.max(1, Math.ceil(L / s - 1e-6)); return Array.from({ length: n + 1 }, (_, i) => +((L * i) / n).toFixed(3)); };
const splitLargest = (xs) => {
  let k = 0;
  xs.forEach((x, i) => { if (i && x - xs[i - 1] > xs[k + 1] - xs[k]) k = i - 1; });
  return [...xs.slice(0, k + 1), +((xs[k] + xs[k + 1]) / 2).toFixed(3), ...xs.slice(k + 1)];
};
const mods = (xs) => xs.slice(1).map((x, i) => x - xs[i]);

const Shapes = ({ el, H }) => (
  <>
    {el.opaque.map((g, i) => <path key={`o${i}-${g.outer[0]}`} d={path(g, H)} fill="#3f3f46" fillRule="evenodd" stroke="#52525b" strokeWidth={0.01} />)}
    {el.glazed.map((g, i) => <path key={`g${i}-${g.outer[0]}`} d={path(g, H)} fill="#38a0e6" fillOpacity={0.55} fillRule="evenodd" stroke="#7dd3fc" strokeWidth={0.012} />)}
    {el.openings.map(([x0, y0, x1, y1]) => <rect key={`op${x0}-${y0}`} x={x0} y={H - y1} width={x1 - x0} height={y1 - y0} fill="none" stroke="#22d3ee" strokeWidth={0.015} strokeDasharray="0.08 0.05" />)}
  </>
);

const Dims = ({ grid, H, fs }) => (
  <>
    {mods(grid.mullions).map((w, i) => w >= fs * 2.4 && <text key={`dw${grid.mullions[i]}`} x={grid.mullions[i] + w / 2} y={-0.25} fontSize={fs} fill="#fbbf24" textAnchor="middle">{Math.round(w * 1000)}</text>)}
    {mods(grid.transoms).map((h, i) => h >= fs * 1.2 && <text key={`dh${grid.transoms[i]}`} x={grid.mullions.at(-1) + 0.2} y={H - grid.transoms[i] - h / 2} fontSize={fs} fill="#fbbf24" dominantBaseline="middle">{Math.round(h * 1000)}</text>)}
  </>
);

const useGridDrag = (svgRef, el, grid, setGrid, setSel) => {
  const [drag, setDrag] = useState(null);
  const snaps = useMemo(() => ({ mullions: el.openings.flatMap((o) => [o[0], o[2]]), transoms: el.openings.flatMap((o) => [o[1], o[3]]) }), [el]);
  const toLocal = (e) => { const p = svgRef.current.createSVGPoint(); p.x = e.clientX; p.y = e.clientY; return p.matrixTransform(svgRef.current.getScreenCTM().inverse()); };
  const start = (key, i) => (e) => { e.stopPropagation(); e.target.setPointerCapture?.(e.pointerId); setSel({ key, i }); setDrag({ key, i }); };
  const move = (e) => {
    if (!drag) return;
    const p = toLocal(e);
    const xs = grid[drag.key], L = drag.key === "mullions" ? el.width_m : el.height_m;
    let v = drag.key === "mullions" ? p.x : el.height_m - p.y;
    const s = snaps[drag.key].find((q) => Math.abs(q - v) < SNAP);
    if (s !== undefined) v = s;
    v = Math.min(drag.i < xs.length - 1 ? xs[drag.i + 1] - MIN : L, Math.max(drag.i > 0 ? xs[drag.i - 1] + MIN : 0, v));
    setGrid((g) => ({ ...g, [drag.key]: g[drag.key].map((x, j) => (j === drag.i ? +v.toFixed(3) : x)) }));
  };
  return { start, move, end: () => setDrag(null), drag };
};

const GridLines = ({ grid, H, sel, start }) => (
  <>
    {grid.transoms.map((y, i) => (
      <g key={`t${i}`} onPointerDown={start("transoms", i)} className="cursor-ns-resize" data-testid={`band-elev-transom-${i}`}>
        <line x1={grid.mullions[0]} x2={grid.mullions.at(-1)} y1={H - y} y2={H - y} stroke="transparent" strokeWidth={16} vectorEffect="non-scaling-stroke" />
        <line x1={grid.mullions[0]} x2={grid.mullions.at(-1)} y1={H - y} y2={H - y} stroke={sel?.key === "transoms" && sel.i === i ? "#fff" : "#f97316"} strokeWidth={sel?.key === "transoms" && sel.i === i ? 3.5 : 1.6} vectorEffect="non-scaling-stroke" />
      </g>
    ))}
    {grid.mullions.map((x, i) => (
      <g key={`m${i}`} onPointerDown={start("mullions", i)} className="cursor-ew-resize" data-testid={`band-elev-mullion-${i}`}>
        <line x1={x} x2={x} y1={H - grid.transoms.at(-1)} y2={H - grid.transoms[0]} stroke="transparent" strokeWidth={16} vectorEffect="non-scaling-stroke" />
        <line x1={x} x2={x} y1={H - grid.transoms.at(-1)} y2={H - grid.transoms[0]} stroke={sel?.key === "mullions" && sel.i === i ? "#fff" : "#f59e0b"} strokeWidth={sel?.key === "mullions" && sel.i === i ? 3.5 : 2} vectorEffect="non-scaling-stroke" />
      </g>
    ))}
  </>
);

const Stats = ({ grid, limit }) => {
  const mw = Math.max(...mods(grid.mullions)), mh = Math.max(...mods(grid.transoms));
  return (
    <div className="flex flex-wrap gap-x-4 gap-y-1 font-mono text-[11px] text-zinc-400" data-testid="band-elev-stats">
      <span>Grid <b className="text-zinc-100">{grid.mullions.length - 1} × {grid.transoms.length - 1}</b></span>
      <span className={mw > limit + 1e-6 ? "text-red-400" : ""} data-testid="band-elev-max-module">Max module <b className="text-zinc-100">{Math.round(mw * 1000)} × {Math.round(mh * 1000)} mm</b>{mw > limit + 1e-6 && ` (exceeds ${limit * 1000} mm limit)`}</span>
      <span>Width <b className="text-zinc-100">{(grid.mullions.at(-1) - grid.mullions[0]).toFixed(2)} m</b></span>
    </div>
  );
};

export const BandElevation = ({ project, zone, onClose, onDone, onOpenDesign }) => {
  const [brand, setBrand] = useState("bhoruka");
  const [el, setEl] = useState(null);
  const [grid, setGrid] = useState(null);
  const [sel, setSel] = useState(null);
  const [sp, setSp] = useState({ m: "", t: "" });
  const [busy, setBusy] = useState(null);
  const svgRef = useRef(null);
  const url = `/projects/${project.id}/model/zones/${zone.id}/elevation`;
  useEffect(() => {
    let live = true;
    setEl(null);
    api.get(url, { params: { brand } }).then(({ data }) => { if (live) { setEl(data); setGrid((g) => g || data.grid || data.auto_grid); } })
      .catch((e) => { if (live) { toast.error(errMsg(e, "Could not unfold this band")); onClose(); } });
    return () => { live = false; };
  }, [url, brand]); // eslint-disable-line react-hooks/exhaustive-deps
  const scrollRef = useRef(null);
  const pan = useRef(null);
  const { start, move, end, drag } = useGridDrag(svgRef, el || { openings: [] }, grid || {}, setGrid, setSel);
  if (!el || !grid) return <div className="flex items-center gap-2 rounded-lg border border-zinc-800 p-4 text-xs text-zinc-400" data-testid="band-elev-loading"><Loader2 className="h-4 w-4 animate-spin" />Unfolding {zone.label}…</div>;
  const stop = () => { pan.current = null; end(); };
  const H = el.height_m, W = el.width_m, fs = Math.max(0.14, Math.max(W, H) / 70);
  const act = (k, fn) => async () => { setBusy(k); try { await fn(); } catch (e) { toast.error(errMsg(e, "Action failed")); } finally { setBusy(null); } };
  const del = () => { if (!sel || sel.i === 0 || sel.i === grid[sel.key].length - 1) return toast.error("Select an inner line to delete (edge lines stay)"); setGrid((g) => ({ ...g, [sel.key]: g[sel.key].filter((_, j) => j !== sel.i) })); setSel(null); };
  const save = act("save", async () => { await api.put(`${url}/grid`, grid); toast.success("Grid saved"); });
  const dxf = act("dxf", async () => {
    const res = await api.post(`${url}/dxf`, grid, { responseType: "blob" });
    const a = document.createElement("a"); a.href = URL.createObjectURL(res.data); a.download = `${zone.label.replace(/\s/g, "_")}_elevation.dxf`; a.click(); URL.revokeObjectURL(a.href);
  });
  const send = act("send", async () => {
    const { data } = await api.post(`${url}/design`, { ...grid, brand });
    toast.success(`${data.updated ? "Updated" : "Created"} design · ${data.status}`, { action: onOpenDesign && { label: "Open", onClick: () => onOpenDesign(data.design_id) } });
    setEl((e) => ({ ...e, design_id: data.design_id }));
    onDone();
  });
  return (
    <div className="space-y-3 rounded-lg border border-amber-500/30 bg-[#121215] p-4" data-testid="band-elevation-panel">
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <h3 className="font-[Manrope] text-lg font-bold">Band elevation · {el.label}</h3>
          <p className="text-xs text-zinc-500" data-testid="band-elev-size">{W.toFixed(2)} × {H.toFixed(2)} m · glazed {el.glazed_m2} m² · cladding {el.opaque_m2} m² (unfolded, overlaps counted once) · {el.openings.length} openings · drag lines (snap to opening edges), click to select</p>
        </div>
        <button onClick={onClose} data-testid="band-elev-close" className="shrink-0 text-zinc-500 transition-colors hover:text-white"><X className="h-4 w-4" /></button>
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <Select value={brand} onValueChange={setBrand}>
          <SelectTrigger data-testid="band-elev-brand" className="h-8 w-48 border-zinc-800 bg-zinc-900 text-xs"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="bhoruka" className="text-xs">Bhoruka · max 1500 mm</SelectItem><SelectItem value="bway" className="text-xs">BWAY · max 1200 mm</SelectItem></SelectContent>
        </Select>
        <Button size="sm" variant="outline" className="h-8 border-zinc-700" data-testid="band-elev-auto-btn" onClick={() => { setGrid(el.auto_grid); setSel(null); }}><RotateCcw className="mr-1 h-3.5 w-3.5" />Auto grid</Button>
        <Button size="sm" variant="outline" className="h-8 border-zinc-700" data-testid="band-elev-add-mullion" onClick={() => setGrid((g) => ({ ...g, mullions: splitLargest(g.mullions) }))}><Plus className="mr-1 h-3.5 w-3.5" />Mullion</Button>
        <Button size="sm" variant="outline" className="h-8 border-zinc-700" data-testid="band-elev-add-transom" onClick={() => setGrid((g) => ({ ...g, transoms: splitLargest(g.transoms) }))}><Plus className="mr-1 h-3.5 w-3.5" />Transom</Button>
        <Button size="sm" variant="outline" className="h-8 border-zinc-700" data-testid="band-elev-delete-line" disabled={!sel} onClick={del}><Trash2 className="mr-1 h-3.5 w-3.5" />Delete line</Button>
        <div className="flex items-center gap-1">
          <Input data-testid="band-elev-spacing-m" placeholder="mullion m" value={sp.m} onChange={(e) => setSp({ ...sp, m: e.target.value })} className="h-8 w-24 border-zinc-800 bg-zinc-900 text-xs" />
          <Input data-testid="band-elev-spacing-t" placeholder="transom m" value={sp.t} onChange={(e) => setSp({ ...sp, t: e.target.value })} className="h-8 w-24 border-zinc-800 bg-zinc-900 text-xs" />
          <Button size="sm" variant="outline" className="h-8 border-zinc-700" data-testid="band-elev-uniform-btn" onClick={() => {
            const m = parseFloat(sp.m), t = parseFloat(sp.t);
            if (!(m >= MIN) && !(t >= MIN)) return toast.error("Enter a spacing of at least 0.1 m");
            setGrid((g) => ({ mullions: m >= MIN ? uniform(W, m) : g.mullions, transoms: t >= MIN ? uniform(H, t) : g.transoms })); setSel(null);
          }}>Uniform</Button>
        </div>
      </div>
      <Stats grid={grid} limit={el.limit_m} />
      <div ref={scrollRef} className="overflow-x-auto rounded-md border border-zinc-800 bg-[#0c0c0f]" data-testid="band-elev-scroll">
        <svg ref={svgRef} data-testid="band-elev-svg" viewBox={`${-PAD} ${-PAD} ${W + 2 * PAD} ${H + 2 * PAD}`} style={{ minWidth: Math.min(1400, Math.max(360, (W / Math.max(H, 1)) * 220)) }} className="max-h-[480px] w-full touch-none select-none"
          onPointerMove={(e) => (pan.current && !drag ? (scrollRef.current.scrollLeft = pan.current.s - (e.clientX - pan.current.x)) : move(e))}
          onPointerUp={stop} onPointerLeave={stop} onPointerCancel={stop}
          onPointerDown={(e) => { setSel(null); pan.current = { x: e.clientX, s: scrollRef.current.scrollLeft }; }}>
          <rect x={0} y={0} width={W} height={H} fill="#18181b" />
          <Shapes el={el} H={H} />
          <GridLines grid={grid} H={H} sel={sel} start={start} />
          <Dims grid={grid} H={H} fs={fs} />
          <text x={0} y={H + 0.45} fontSize={fs} fill="#71717a">{`${el.z0 >= 0 ? "+" : ""}${el.z0} m`}</text>
          <text x={0} y={-0.55} fontSize={fs} fill="#71717a">{`${el.z1 >= 0 ? "+" : ""}${el.z1} m · ${el.orientation} facade`}</text>
        </svg>
      </div>
      <div className="flex flex-wrap justify-end gap-2">
        <Button size="sm" variant="outline" className="border-zinc-700" data-testid="band-elev-save-btn" disabled={!!busy} onClick={save}>{busy === "save" ? <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" /> : <Save className="mr-1.5 h-3.5 w-3.5" />}Save grid</Button>
        <Button size="sm" variant="outline" className="border-zinc-700" data-testid="band-elev-dxf-btn" disabled={!!busy} onClick={dxf}>{busy === "dxf" ? <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" /> : <Download className="mr-1.5 h-3.5 w-3.5" />}DXF</Button>
        <Button size="sm" className="bg-amber-500 text-black hover:bg-amber-400" data-testid="band-elev-send-btn" disabled={!!busy} onClick={send}>{busy === "send" ? <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" /> : <Send className="mr-1.5 h-3.5 w-3.5" />}{el.design_id ? "Update design" : "Send to design editor"}</Button>
      </div>
    </div>
  );
};
